import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  resolveProviderFixtures,
  type ProviderFixtureResolution,
} from "@/lib/sports/providerFixtures.functions";
import { MATCHES } from "@/lib/matches";
import { supabase } from "@/integrations/supabase/client";

interface TeamMappingRow {
  fifa_code: string;
  api_football_team_id: number | null;
  enabled: boolean;
}

/**
 * Resolve real API-Football fixture ids for every entry in MATCHES by
 * matching each match's FIFA team codes (e.g. MEX/ARG) to api-football
 * numeric team ids via `national_teams_provider_map`, then asking the
 * server function to find the next/live fixture for each pair.
 *
 * Returns a map keyed by MatchInfo.id. Missing entries mean no real
 * fixture was found — the caller should fall back to the synthetic id
 * (DemoProvider).
 */
export function useProviderFixtureIds(
  enabled: boolean,
): Record<string, ProviderFixtureResolution> {
  const fn = useServerFn(resolveProviderFixtures);
  const { data } = useQuery({
    queryKey: ["provider-fixture-ids", MATCHES.map((m) => m.id).join(",")],
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const codes = Array.from(
        new Set(MATCHES.flatMap((m) => [m.homeCode, m.awayCode])),
      );
      const { data: rows, error } = await supabase
        .from("national_teams_provider_map" as never)
        .select("fifa_code,api_football_team_id,enabled")
        .in("fifa_code", codes);
      if (error) return {} as Record<string, ProviderFixtureResolution>;
      const map = new Map<string, TeamMappingRow>();
      ((rows ?? []) as unknown as TeamMappingRow[]).forEach((r) =>
        map.set(r.fifa_code.toUpperCase(), r),
      );
      const pairs = MATCHES.flatMap((m) => {
        const h = map.get(m.homeCode.toUpperCase());
        const a = map.get(m.awayCode.toUpperCase());
        if (
          !h?.enabled ||
          !a?.enabled ||
          h.api_football_team_id == null ||
          a.api_football_team_id == null
        ) {
          return [];
        }
        return [
          {
            id: m.id,
            homeId: h.api_football_team_id,
            awayId: a.api_football_team_id,
          },
        ];
      });
      if (pairs.length === 0) return {} as Record<string, ProviderFixtureResolution>;
      return (await fn({ data: { pairs } })) as Record<
        string,
        ProviderFixtureResolution
      >;
    },
  });
  return data ?? {};
}